import React, {useEffect, useRef, useState} from 'react';
import {Frequency, Options, RRule} from "rrule";
import {ParsedOptions} from "rrule/dist/esm/types";
import ByHourInput from "./ByHourInput";
import ByWeekdayCheckboxList, {Weekday} from "./ByWeekdayCheckboxList";
import ByMonthDaySelect from "./ByMonthDaySelect";
import {DateTime} from "luxon";
import {concatenate} from "../../../services/string";

// Types ----

enum EndMode {NEVER, UNTIL, COUNT}

type RepetitionBuilderProps = {
  timezone?: string
  startDate: Date
  rule: RRule | null | undefined
  onChange: (rule: RRule | null) => any
  disabled?: boolean
  enableForever?: boolean
  untilMax?: string
}

// Helpers ----

const sanitisedRule = (rule: RRule): RRule => {
  const options = {...rule.options} as Partial<Options>

  delete options.dtstart
  delete options.byminute
  delete options.bysecond
  delete options.byeaster

  if (options.freq !== Frequency.HOURLY) {
    delete options.byhour
  }

  return new RRule(options)
}

const determineEndMode = (options?: ParsedOptions): EndMode => {
  return options?.count ? EndMode.COUNT : (options?.until ? EndMode.UNTIL : EndMode.NEVER)
}

const pluralise = (word: string, count: number): string => {
  return count === 1 ? word : `${word}s`
}

const defaultRuleOptions: Partial<Options> = {freq: Frequency.DAILY};

// Component ----

function RepetitionBuilder({timezone = 'local', startDate, rule, onChange, disabled, enableForever = true, untilMax}: RepetitionBuilderProps) {
  const [currentRule, setCurrentRule] = useState<RRule | null>(rule ?? new RRule(defaultRuleOptions));

  const [previousTimezone, setPreviousTimezone] = useState<string>(timezone);

  const untilDateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if(currentRule?.options.until && timezone !== previousTimezone) {
      // Apply the timezone to the date already selected so the end of the day can be determined correctly
      const untilDate = DateTime
        .fromJSDate(currentRule.options.until)
        .setZone(timezone, {keepLocalTime: true})
        .endOf('day')
        .toJSDate();

      const newRule = new RRule({...currentRule?.options, until: untilDate});

      setCurrentRule(newRule)
      onChange(sanitisedRule(newRule))
      setPreviousTimezone(timezone);
    }
  }, [timezone, previousTimezone, currentRule, onChange]);

  // Force 'until' input validation on blur
  useEffect(() => {
    const untilDateInput = untilDateRef.current;

    function reportValidity() {
      untilDateRef.current?.reportValidity();
    }

    untilDateInput?.addEventListener('blur', reportValidity)

    return () => untilDateInput?.removeEventListener('blur', reportValidity);
  }, []);

  function updateRule(changedOptions: Partial<Options>) {
    const newRule = new RRule(changedOptions)
    setCurrentRule(newRule)
    onChange(sanitisedRule(newRule))
  }

  function changeInterval(interval: number) {
    updateRule({...currentRule?.options, interval: interval})
  }

  function selectFrequency(frequency: Frequency) {
    const options = {...currentRule?.options, freq: frequency} as Partial<Options>

    if (frequency !== Frequency.HOURLY) {
      delete options.byhour
    }

    if (frequency !== Frequency.WEEKLY) {
      delete options.byweekday
    }

    if (frequency === Frequency.MONTHLY) {
      options.bymonthday = startDate.getDate()
    }

    updateRule(options)
  }

  function changeHours(hours: number[]) {
    updateRule({...currentRule?.options, byhour: hours, byminute: [], bysecond: []})
  }

  function selectEndMode(endMode: EndMode) {
    const options = {...currentRule?.options}
    delete options.count
    delete options.until

    switch (endMode) {
      case EndMode.COUNT:
        options.count = 1
        break
      case EndMode.UNTIL:
        options.until = new Date()
        break
    }

    updateRule(options)
  }

  function changeCount(count: number) {
    updateRule({...currentRule?.options, count: count})
  }

  function changeUntil(until: string) {
    // The date input can return an empty string if it is not valid
    if (!until) return;

    // Ensure the until date is in YYYY-MM-DD format
    const [year, month, day] = until.split('-');
    const yearSlice = year.slice(0, 4);
    const newUntil = `${yearSlice}-${month}-${day}`;

    updateRule({
      ...currentRule?.options,
      until: DateTime.fromISO(newUntil, {zone: timezone}).endOf('day').toJSDate()
    });
  }

  function selectWeekdays(weekdays: Weekday[]) {
    updateRule({...currentRule?.options, byweekday: weekdays})
  }

  function selectMonthDay(monthday: number | null, weekday: number | null, position: number | null) {
    const options = {...currentRule?.options} as Partial<Options>

    if (monthday) {
      options.bymonthday = monthday
      options.byweekday = null
      options.bysetpos = null
    } else {
      options.bymonthday = null
      options.byweekday = weekday
      options.bysetpos = position
    }

    updateRule(options)
  }

  const weekday = startDate.getDay() === 0 ? 6 : (startDate.getDay() - 1);

  return (
    <>
      <div className="flex space-x-2 items-center">
        <span className="text-sm">Every</span>

        <input
          type="number"
          name="interval"
          id="input-interval"
          step={1}
          min={1}
          value={currentRule?.options.interval ?? 1}
          onChange={event => changeInterval(Number(event.currentTarget.value))}
          className={concatenate(
            "block w-12 rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm",
            disabled ? 'text-gray-500 cursor-not-allowed' : '',
            )}
          disabled={disabled}
        />

        <select
          name="frequency"
          id="select-frequency"
          value={currentRule?.options.freq ?? Frequency.DAILY}
          onChange={event => selectFrequency(Number(event.currentTarget.value) as Frequency)}
          className={concatenate(
            "block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-brand focus:outline-none focus:ring-brand sm:text-sm",
            disabled ? 'cursor-not-allowed' : '',
          )}
          disabled={disabled}
        >
          <option value={Frequency.HOURLY}>
            {pluralise('hour', currentRule?.options.interval ?? 1)}
          </option>
          <option value={Frequency.DAILY}>
            {pluralise('day', currentRule?.options.interval ?? 1)}
          </option>
          <option value={Frequency.WEEKLY}>
            {pluralise('week', currentRule?.options.interval ?? 1)}
          </option>
          <option value={Frequency.MONTHLY}>
            {pluralise('month', currentRule?.options.interval ?? 1)}
          </option>
          <option value={Frequency.YEARLY}>
            {pluralise('year', currentRule?.options.interval ?? 1)}
          </option>
        </select>

        <select
          name="end"
          id="select-end"
          value={determineEndMode(currentRule?.options)}
          onChange={event => selectEndMode(Number(event.currentTarget.value) as EndMode)}
          className={concatenate(
            "block rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-brand focus:outline-none focus:ring-brand sm:text-sm",
            disabled ? 'cursor-not-allowed' : '',
          )}
          disabled={disabled}
        >
          {enableForever && <option value={EndMode.NEVER}>forever</option>}
          <option value={EndMode.UNTIL}>until</option>
          <option value={EndMode.COUNT}>for</option>
        </select>

        {!!currentRule?.options.count &&
          <>
            <input
              type="number"
              name="count"
              id="input-count"
              step={1}
              min={1}
              value={currentRule?.options.count ?? 1}
              onChange={event => changeCount(Number(event.currentTarget.value))}
              className={concatenate(
                "block w-12 rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm",
                disabled ? 'text-gray-500 cursor-not-allowed' : '',
              )}
              disabled={disabled}
            />

            <span className="text-sm">occurrences</span>
          </>
        }

        {!!currentRule?.options.until &&
          <input
            ref={untilDateRef}
            type="date"
            name="until"
            id="input-until"
            value={DateTime.fromJSDate(currentRule.options.until).setZone(timezone).toISODate()}
            onChange={event => changeUntil(event.currentTarget.value)}
            className={concatenate(
              "block rounded-md border-gray-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm",
              disabled ? 'text-gray-500 cursor-not-allowed' : '',
            )}
            disabled={disabled}
            max={untilMax}
          />
        }
      </div>

      {/* ByHour selector */}
      {currentRule?.options.freq === Frequency.HOURLY &&
        <div className="mt-2 flex space-x-2 items-center">
          <span className="text-sm">between</span>

          <ByHourInput
            hours={currentRule?.options.byhour}
            onChange={changeHours}
            disabled={disabled}
          />
        </div>
      }

      {/* ByWeekDay selector */}
      {currentRule?.options.freq === Frequency.WEEKLY &&
        <div className="mt-2 lg:flex lg:space-x-2 lg:items-center">
          <span className="text-sm">on</span>

          <ByWeekdayCheckboxList
            days={currentRule?.options.byweekday as Weekday[] ?? [weekday]}
            onChange={selectWeekdays}
            disabled={disabled}
          />
        </div>
      }

      {/* BySetPos / ByMonthDay selector */}
      {currentRule?.options.freq === Frequency.MONTHLY &&
        <div className="mt-2 flex space-x-2 items-center">
          <span className="text-sm">on</span>

          <ByMonthDaySelect
            date={startDate}
            selected={currentRule?.options.bymonthday.length ? 'bymonthday' : 'bysetpos'}
            onChange={(option, monthday, weekday, position) => selectMonthDay(monthday, weekday, position)}
            disabled={disabled}
          />
        </div>
      }
    </>
  );
}

// Export ----

export default RepetitionBuilder;